/**
 * Wording resolver for the `ok_with_hints` MIDI quality toast.
 *
 * Spec: `docs/spec/midi-quality-feedback.md` §3.1 and §6.4.
 *
 * When the analyzer hesitates but the user has no real disambiguation to do
 * (i.e. fewer than 2 candidate tracks), the back returns
 * `quality.kind === "ok_with_hints"`. The file opens directly, and we want to
 * surface a non-alarmist, informative toast at the Studio mount so the user
 * is aware that the import might not be perfect.
 *
 * This module is intentionally UI-free so it can be unit-tested in isolation
 * and reused by any future surface (e.g. a banner inside Piece Overview).
 */
import type { QualityFlag } from "@/lib/music/analyzer-types";

/**
 * Priority order of the hint flags, mirroring the wording table of the
 * spec §6.4. The first flag found in this list wins. `no_piano` and
 * `all_drums` share the same wording (both mean "we didn't find a piano").
 *
 * The score flags are interleaved by how much they change what the user will
 * hear: a jump that is not followed cuts the piece short, so it outranks
 * everything but "there is no piano here at all"; unread hands change how the
 * piece is shown, not what it contains; cue notes are a nuance.
 */
const HINT_PRIORITY: readonly QualityFlag[] = [
  "no_piano",
  "all_drums",
  "jumps_not_followed",
  "drums_dominant",
  "vocal_dominant",
  "arrangement",
  "single_track_no_program",
  "polyphony_low",
];

/**
 * FR wording per dominant hint. Tutoiement, never alarmist, no emoji and no
 * words like "erreur" / "échec" / "problème" : the file did open, we just
 * want to flag a possible imperfection.
 */
const MESSAGES: Record<QualityFlag, string> = {
  no_piano:
    "On n'a pas trouvé de piano clairement identifié. On a importé la piste disponible.",
  all_drums:
    "On n'a pas trouvé de piano clairement identifié. On a importé la piste disponible.",
  drums_dominant:
    "Ton fichier contient surtout de la batterie. On a gardé la piste mélodique.",
  vocal_dominant:
    "Ton fichier semble plutôt vocal. On a gardé la piste instrumentale.",
  arrangement:
    "Ce morceau mélange plusieurs instruments (basse, batterie, guitares…). L'import au piano peut ne pas être parfait.",
  single_track_no_program:
    "Ton fichier ne contient qu'une seule piste. On l'a importée telle quelle.",
  polyphony_low: "Ce fichier contient peu de notes. On l'a importé tel quel.",
  jumps_not_followed:
    "Ta partition renvoie au début (D.C. ou D.S.). On ne suit pas ce renvoi : le morceau s'arrête à la dernière mesure écrite.",
};

/**
 * Resolves the FR wording shown in the `ok_with_hints` toast at the Studio
 * mount, and in the warning block of the upload modal. Priority order is
 * {@link HINT_PRIORITY}.
 *
 * If no recognized flag is present in the input, returns `null`, the caller
 * skips the toast entirely. Callers are expected to have scoped the flags to
 * the file's format first (`lib/upload/quality-scope.ts`), so a flag reaching
 * here is one that can describe the file.
 */
export function resolveOkWithHintsToastMessage(
  hints: readonly QualityFlag[],
): string | null {
  if (hints.length === 0) {
    return null;
  }

  for (const flag of HINT_PRIORITY) {
    if (hints.includes(flag)) {
      return MESSAGES[flag];
    }
  }

  return null;
}
